import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Announcements,
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverlay,
  DragMoveEvent,
  DragEndEvent,
  DragOverEvent,
  MeasuringStrategy,
  DropAnimation,
  Modifier,
  defaultDropAnimation,
  MouseSensor,
  TouchSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import {
  buildTree,
  flattenTree,
  getProjection,
  removeChildrenOf,
  setProperty,
} from "./utilities";
import type { FlattenedItem, SensorContext, TreeItems } from "./types";
import { CSS } from "@dnd-kit/utilities";
import { sortableTreeKeyboardCoordinates } from "./keyboardCoordinates";
import { SortableTreeItem } from "./components/TreeItem/SortableTreeItem";
import { CustomLinkOptionsProps } from "./components/TreeItem/CustomLinkOptions";
import { newToastEvent } from "../../utils/ToastUtil";
import { createEmptyLink } from "../Sidebar/CustomLinks/CustomLinks2";

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: "ease-out",
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

interface Props extends CustomLinkOptionsProps {
  collapsible?: boolean;
  items: TreeItems;
  setItems: React.Dispatch<React.SetStateAction<TreeItems>>;
  indentationWidth?: number;
  indicator?: boolean;

  itemStyle?: CSSProperties;
  onOrderChange: (items: TreeItems, appUpdated: string | null) => void;
  maxDepth?: number;
  reorderDisabled?: boolean;
  onCollapse?: (id: string, action: "open" | "close") => void;
  setExistingCloudApp: React.Dispatch<React.SetStateAction<any>>;
  setExistingPageTo: React.Dispatch<React.SetStateAction<any>>;
  setForceUpdateDrawer: React.Dispatch<React.SetStateAction<any>>;
}

export function NestedSortable({
  collapsible,
  items,
  setItems,
  indicator = false,
  indentationWidth = 24,
  onOrderChange,
  itemStyle = { width: "100%" },
  maxDepth = 1,
  reorderDisabled = false,
  onCollapse,
  setExistingPageTo,
  setExistingCloudApp,
  setForceUpdateDrawer,
  ...props
}: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [currentPosition, setCurrentPosition] = useState<{
    parentId: string | null;
    overId: string;
  } | null>(null);

  // Flattens the tree, (plaaces child items alongside parent items)
  const flattenedItems = useMemo(() => {
    const flattenedTree = flattenTree(items);
    const collapsedItems = flattenedTree.reduce<string[]>(
      (acc, { children, collapsed, id }) =>
        collapsed && children.length ? [...acc, id] : acc,
      []
    );

    return removeChildrenOf(
      flattenedTree,
      activeId ? [activeId, ...collapsedItems] : collapsedItems
    );
  }, [activeId, items]);

  // Gets the depth of the dragged item and the depth of the drop target
  const projected =
    activeId && overId
      ? getProjection(
          flattenedItems,
          activeId,
          overId,
          offsetLeft,
          indentationWidth
        )
      : null;

  const sensorContext: SensorContext = useRef({
    items: flattenedItems,
    offset: offsetLeft,
  });
  const [coordinateGetter] = useState(() =>
    sortableTreeKeyboardCoordinates(sensorContext, indicator, indentationWidth)
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require the pointer to move by 5 pixels before activating
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    }),
    useSensor(TouchSensor, {
      // Require the touch to move by 5 pixels before activating
      activationConstraint: {
        distance: 5,
        delay: 100,
      },
    }),
    useSensor(MouseSensor, {
      // Require the mouse to move by 5 pixels before activating
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const sortedIds = useMemo(
    () => flattenedItems.map(({ id }) => id),
    [flattenedItems]
  );
  const activeItem = activeId
    ? flattenedItems.find(({ id }) => id === activeId)
    : null;

  useEffect(() => {
    sensorContext.current = {
      items: flattenedItems,
      offset: offsetLeft,
    };
  }, [flattenedItems, offsetLeft]);

  const announcements: Announcements = {
    onDragStart({ active }) {
      return `Picked up ${active.id}.`;
    },
    onDragMove({ active, over }) {
      return getMovementAnnouncement(
        "onDragMove",
        active.id as string,
        over?.id as string
      );
    },
    onDragOver({ active, over }) {
      return getMovementAnnouncement(
        "onDragOver",
        active.id as string,
        over?.id as string
      );
    },
    onDragEnd({ active, over }) {
      return getMovementAnnouncement(
        "onDragEnd",
        active.id as string,
        over?.id as string
      );
    },
    onDragCancel({ active }) {
      return `Moving was cancelled. ${active.id} was dropped in its original position.`;
    },
  };

  return (
    <DndContext
      accessibility={{ announcements }}
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={measuring}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={sortedIds}
        strategy={verticalListSortingStrategy}
        disabled={reorderDisabled}
      >
        {flattenedItems.map((item) => {
          const { id, name, children, collapsed, depth } = item;

          return (
            <SortableTreeItem
              key={id}
              id={id}
              text={name}
              item={item}
              depth={
                id === activeId && projected
                  ? projected.depth < maxDepth + 1
                    ? projected.depth
                    : 1
                  : depth
              }
              indentationWidth={indentationWidth}
              indicator={indicator}
              collapsed={Boolean(collapsed && children.length)}
              onCollapse={
                collapsible && children.length
                  ? () => handleCollapse(id)
                  : undefined
              }
              itemStyle={itemStyle}
              setExistingCloudApp={setExistingCloudApp}
              setExistingPageTo={setExistingPageTo}
              setForceUpdateDrawer={setForceUpdateDrawer}
              {...props}
            />
          );
        })}
        {createPortal(
          <DragOverlay
            dropAnimation={dropAnimationConfig}
            modifiers={indicator ? [adjustTranslate] : undefined}
          >
            {activeId && activeItem ? (
              <SortableTreeItem
                id={activeId}
                depth={activeItem.depth}
                item={activeItem}
                clone
                text={activeItem.name}
                indentationWidth={indentationWidth}
                itemStyle={itemStyle}
                isDragging={true}
                setExistingPageTo={setExistingPageTo}
                setExistingCloudApp={setExistingCloudApp}
                setForceUpdateDrawer={setForceUpdateDrawer}
                {...props}
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </SortableContext>
    </DndContext>
  );

  function handleDragStart({ active: { id: activeId } }: DragStartEvent) {
    setActiveId(activeId as string);
    setOverId(activeId as string);

    const activeItem = flattenedItems.find(({ id }) => id === activeId);

    if (activeItem) {
      setCurrentPosition({
        parentId: activeItem.parentId,
        overId: activeId as string,
      });
    }

    document.body.style.setProperty("cursor", "grabbing");
  }

  function handleDragMove({ delta }: DragMoveEvent) {
    setOffsetLeft(delta.x);
  }

  function handleDragOver({ over }: DragOverEvent) {
    setOverId((over?.id as string) ?? null);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    resetState();

    if (projected && over) {
      let { depth, parentId } = projected;

      // Ensure that the depth is not greater than the max depth
      // If so, go back to max depth level / item
      if (depth >= maxDepth + 1) {
        const parentItem = flattenedItems.find(({ id }) => id === parentId);
        parentId = parentItem?.parentId!;
        depth = maxDepth;
      }

      let appUpdated: string | null = null;

      const originalItems = flattenTree(items);
      const clonedItems: FlattenedItem[] = structuredClone(originalItems);

      const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
      const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);

      const overTreeItem = clonedItems[overIndex];
      const activeTreeItem = clonedItems[activeIndex];

      const activeParentItem = activeTreeItem.parentId
        ? clonedItems.find((i) => i.id === activeTreeItem.parentId)
        : null;
      const overParentItem = overTreeItem.parentId
        ? clonedItems.find((i) => i.id === overTreeItem.parentId)
        : null;

      // Reject a folder from being moved to a depth greater than 0.
      if (activeTreeItem?.children?.length && depth > 0) {
        return;
      }

      // No movement occurred. (Notice the depth has not changed too)
      if (
        overTreeItem.id === activeTreeItem.id &&
        depth === activeTreeItem.depth
      ) {
        return;
      }

      // User tried to reorder an Installed Cloud App's pages
      if (activeParentItem?.data?.Type === "CloudAppViewer") {
        return newToastEvent("warning", "You can't reorder an App's pages");
      }

      // User tried to move an item into an Externally created Cloud App.
      if (
        overParentItem?.data?.Type === "CloudAppViewer" ||
        (overTreeItem.data?.Type === "CloudAppViewer" && depth > 0)
      ) {
        return newToastEvent(
          "warning",
          "You can't move an item into a Cloud App Folder."
        );
      }

      // Ensure we track when a Cloud App is updated, IMPORTANT for the API.
      if (overParentItem?.data?.Type === "CloudAppOwner") {
        appUpdated = overParentItem.id;
      } else if (activeParentItem?.data?.Type === "CloudAppOwner") {
        appUpdated = activeParentItem.id;
      } else if (overTreeItem.data?.Type === "CloudAppOwner" && depth > 0) {
        appUpdated = overTreeItem.id;
      }

      // Check if index above is a valid group (NOTICE that the depth has changed)
      if (
        overTreeItem.id === activeTreeItem.id &&
        depth !== activeTreeItem.depth
      ) {
        let itemAbove: FlattenedItem | undefined = clonedItems[overIndex - 1];

        if (itemAbove?.parentId) {
          itemAbove = clonedItems.find((i) => i.id === itemAbove?.parentId);
        }

        if (!itemAbove) return;

        // User tried to move an item into an External Cloud/Global App, or PageLink.
        if (!["Group", "CloudAppOwner"].includes(itemAbove?.data?.Type || "")) {
          if (itemAbove.data?.Type === "CloudAppViewer") {
            return newToastEvent(
              "warning",
              "You can't move an item into a Cloud App Folder."
            );
          } else {
            return;
          }
        } else {
          if (itemAbove.data?.Type === "CloudAppOwner") {
            appUpdated = itemAbove.id;
          }
        }
      }

      clonedItems[activeIndex] = { ...activeTreeItem, depth, parentId };

      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);
      const newItems = buildTree(sortedItems);

      // ----------- This loop performs THREE tasks -------------
      // 1 - Add a new empty item to the group if the group is empty.
      // 2 - Remove any empty items in groups that have a length greater than 1
      // 3 - Move any nested items that aren't within a group, to the top level
      //     positioned after the item they are nested in
      for (let i = 0; i < newItems.length; i++) {
        const item = newItems[i];
        const type = item?.data?.Type;

        if (["Group", "CloudAppOwner", "CloudAppViewer"].includes(type || "")) {
          // Ensure CloudAppViewer (global app from another Cloud) has not been updated.
          if (type === "CloudAppViewer") {
            const originalItem = originalItems.find(({ id }) => id === item.id);

            if (originalItem?.children?.length !== item.children?.length) {
              return newToastEvent(
                "warning",
                "You can't move an item into a Cloud App Folder."
              );
            }
          }

          if (item.children.length === 1) continue;

          // Add a new empty item to the group if the group is empty.
          if (item.children.length === 0) {
            item.children.push(createEmptyLink());
            continue;
          }
          // Remove any empty items in groups that have a length greater than 1
          item.children = item.children.filter((child) => {
            return (
              child.data?.Type !== "Empty" &&
              child.data?.Type !== "EmptySidebar"
            );
          });

          // Skip to the next item, as this item is MEANT to have children.
          continue;
        }

        // Move any nested items that aren't within a group, to the top level positioned after the item they are nested in
        if (item?.children?.[0]?.name) {
          const childItem = structuredClone(item.children[0]);
          newItems.splice(i + 1, 0, childItem);
          item.children = [];
          i++;
        }
      }

      onOrderChange(newItems, appUpdated);
      setItems(newItems);
    }
  }

  function handleDragCancel() {
    resetState();
  }

  function resetState() {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
    setCurrentPosition(null);

    document.body.style.setProperty("cursor", "");
  }

  function handleCollapse(id: string) {
    const item = flattenedItems.find(({ id: itemId }) => itemId === id);

    if (!item) return;

    onCollapse && onCollapse(id, item.collapsed ? "open" : "close");

    setItems((items) =>
      setProperty(items, id, "collapsed", (value) => {
        return !value;
      })
    );
  }

  function getMovementAnnouncement(
    eventName: string,
    activeId: string,
    overId?: string
  ) {
    if (overId && projected) {
      if (eventName !== "onDragEnd") {
        if (
          currentPosition &&
          projected.parentId === currentPosition.parentId &&
          overId === currentPosition.overId
        ) {
          return;
        } else {
          setCurrentPosition({
            parentId: projected.parentId,
            overId,
          });
        }
      }

      const clonedItems: FlattenedItem[] = structuredClone(flattenTree(items));
      const overIndex = clonedItems.findIndex(({ id }) => id === overId);
      const activeIndex = clonedItems.findIndex(({ id }) => id === activeId);
      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);

      const previousItem = sortedItems[overIndex - 1];

      let announcement;
      const movedVerb = eventName === "onDragEnd" ? "dropped" : "moved";
      const nestedVerb = eventName === "onDragEnd" ? "dropped" : "nested";

      if (!previousItem) {
        const nextItem = sortedItems[overIndex + 1];
        announcement = `${activeId} was ${movedVerb} before ${nextItem.id}.`;
      } else {
        if (projected.depth > previousItem.depth) {
          announcement = `${activeId} was ${nestedVerb} under ${previousItem.id}.`;
        } else {
          let previousSibling: FlattenedItem | undefined = previousItem;
          while (previousSibling && projected.depth < previousSibling.depth) {
            const parentId: string | null = previousSibling.parentId;
            previousSibling = sortedItems.find(({ id }) => id === parentId);
          }

          if (previousSibling) {
            announcement = `${activeId} was ${movedVerb} after ${previousSibling.id}.`;
          }
        }
      }

      return announcement;
    }

    return;
  }
}

const adjustTranslate: Modifier = ({ transform }) => {
  return {
    ...transform,
    y: transform.y - 10,
  };
};
